import * as Sentry from '@sentry/react';
import {
  Check,
  Modal,
  ModalActions,
  ModalContent,
  Pushbutton
} from '@stratumn/atomic';
import to from 'await-to-js';
import graphql from 'babel-plugin-relay/macro';
import { TableRow, UserAvatarLink } from 'components/ui';
import compose from 'lodash.flowright';
import { addTeamBots } from 'mutations';
import PropType from 'prop-types';
import React from 'react';
import injectSheet from 'react-jss';
import { createFragmentContainer } from 'react-relay';
import { SpanType, withSpanAsync } from 'tracing';
import pluralize from 'utils/pluralize';
import { withGqlClient } from 'wrappers';

import styles from './addTeamBotsModal.style';

export class AddTeamBotsModal extends React.PureComponent {
  static propTypes = {
    classes: PropType.object.isRequired,
    environment: PropType.object.isRequired,
    team: PropType.object.isRequired,
    onClose: PropType.func.isRequired
  };

  state = {
    selected: {},
    submitting: false
  };

  getSelectedArr = () => Object.keys(this.state.selected);

  toggleSelected = rowId => () => {
    const selected = { ...this.state.selected };

    if (selected[rowId]) {
      delete selected[rowId];
    } else {
      selected[rowId] = true;
    }

    this.setState({ selected });
  };

  onSubmit = () =>
    withSpanAsync('addTeamBots', SpanType.processing, async () => {
      const { environment, team, onClose } = this.props;

      this.setState({ submitting: true });

      const [err] = await to(
        addTeamBots(environment, team.id, team.rowId, this.getSelectedArr())
      );

      if (err) {
        Sentry.captureException(err);
        this.setState({ submitting: false });
      } else {
        onClose();
      }

      return {};
    });

  renderNode = ({ rowId, name, avatar }) => {
    const { selected } = this.state;
    const { team } = this.props;
    const {
      organization: { rowId: organizationRowId }
    } = team;

    return (
      <TableRow key={rowId}>
        <UserAvatarLink
          avatar={avatar}
          name={name}
          noLink
          organizationRowId={organizationRowId}
          rowId={rowId}
        />
        <Check
          checked={selected[rowId]}
          data-testid="check"
          handleChange={this.toggleSelected(rowId)}
          label={`add ${name} to the Team`}
        />
      </TableRow>
    );
  };

  renderSelector = botsOutsideTeam => {
    const { classes, team } = this.props;

    return (
      <>
        <div className={classes.title}>
          Select the bots to add to the{' '}
          <span className={classes.teamName}>{team.name}</span> Team.
        </div>
        <ul className={classes.botsList}>
          {botsOutsideTeam.map(this.renderNode)}
        </ul>
      </>
    );
  };

  renderAllInOrg = () => {
    const { classes, team } = this.props;

    return (
      <div className={classes.title}>
        All the bots in this organization are already a part of the{' '}
        <span className={classes.teamName}>{team.name}</span> Team.
      </div>
    );
  };

  render() {
    const { onClose, team } = this.props;
    const {
      organization: { bots }
    } = team;
    const { submitting } = this.state;

    const numBotsCount = this.getSelectedArr().length;

    let submitLabel;
    switch (numBotsCount) {
      case 0:
        submitLabel = 'Add Team Bots';
        break;
      default:
        submitLabel = `Add ${pluralize('Team Bot', numBotsCount)}`;
    }

    const botsOutsideTeam = bots.nodes.filter(({ teams }) => {
      for (let idx = 0; idx < teams.nodes.length; idx += 1) {
        const { rowId: teamRowId } = teams.nodes[idx];
        if (teamRowId === team.rowId) {
          return false;
        }
      }
      return true;
    });

    return (
      <Modal
        closeButtonLabel="Cancel"
        handleCollapse={onClose}
        title="Add Bots"
      >
        <ModalContent>
          {botsOutsideTeam.length
            ? this.renderSelector(botsOutsideTeam)
            : this.renderAllInOrg()}
        </ModalContent>
        <ModalActions>
          <Pushbutton
            data-testid="add-team-bots-button"
            disabled={submitting || !numBotsCount}
            onClick={this.onSubmit}
            primary
          >
            {submitLabel}
          </Pushbutton>
        </ModalActions>
      </Modal>
    );
  }
}

export default createFragmentContainer(
  compose(withGqlClient, injectSheet(styles))(AddTeamBotsModal),
  {
    team: graphql`
      fragment addTeamBotsModal_team on Team
      @argumentDefinitions(teamAccountRowId: { type: "BigInt!" }) {
        organization {
          bots {
            nodes {
              rowId
              name
              avatar
              teams {
                nodes {
                  rowId
                }
              }
            }
          }
        }
        account {
          id
        }
        rowId
        id
        name
      }
    `
  }
);
